import type { CSSProperties, ReactElement, ReactNode } from 'react';

/* Something the user must read, including the server's own words.
 *
 * `role` is derived from tone rather than passed, and that is the load-bearing detail: an error must
 * be `role="alert"` so a screen reader announces it without being asked, and a notice must not be,
 * because an assertive live region for informational text interrupts whatever the user was doing.
 * Leaving that to each call site is how half the alerts in a product end up silent and the other half
 * shout.
 *
 * The backend returns RFC 9457 Problem Details with genuinely useful text — "cannot approve a verdict
 * computed against a different schema state" — so this component exists to carry that through rather
 * than replace it with "Something went wrong", which throws away the only actionable part.
 */

export type AlertTone = 'error' | 'warning' | 'notice' | 'ok';

export interface AlertProps {
  readonly tone: AlertTone;
  readonly children: ReactNode;
  readonly title?: string;
}

const COLOUR: Readonly<Record<AlertTone, string>> = {
  error: 'var(--status-error)',
  warning: 'var(--status-warn)',
  notice: 'var(--status-info)',
  // `ok` for a banner that reports a healthy reading — a NORMAL anomaly verdict, say. Announced
  // politely like a notice; the `role` derivation below already covers it.
  ok: 'var(--status-ok)',
};

export function Alert({ tone, title, children }: AlertProps): ReactElement {
  return (
    <div
      // Assertive only for errors. A warning or notice is announced politely, when the user is
      // between actions rather than mid-sentence.
      role={tone === 'error' ? 'alert' : 'status'}
      className="lq-alert"
      // The tone travels as a custom property rather than as a class per tone: one CSS rule, three
      // data points, and the total `Record<AlertTone, …>` below is what makes an unmapped tone a
      // compile error instead of a silently grey error message.
      style={{ '--lq-alert-tone': COLOUR[tone] } as CSSProperties}
    >
      {title !== undefined && <strong className="lq-alert__title">{title}</strong>}
      {children}
    </div>
  );
}
